<script setup lang="ts">
import type { ToastTone } from '~/shared-kernel/presentation/stores/toast.store'
import { useTqToast } from '~/shared-kernel/presentation/composables/useTqToast'

const { t } = useI18n()
const { toasts, dismiss } = useTqToast()

const toastMeta: Record<ToastTone, { icon: string; labelKey: string }> = {
  success: { icon: 'i-lucide-circle-check', labelKey: 'toast.successTitle' },
  error: { icon: 'i-lucide-octagon-alert', labelKey: 'toast.errorTitle' },
  warning: { icon: 'i-lucide-triangle-alert', labelKey: 'toast.warningTitle' },
  info: { icon: 'i-lucide-info', labelKey: 'toast.infoTitle' }
}

function toastRole(tone: ToastTone) {
  return tone === 'error' ? 'alert' : 'status'
}

function toastLiveMode(tone: ToastTone) {
  return tone === 'error' ? 'assertive' : 'polite'
}
</script>

<template>
  <Teleport to="body">
    <section class="tq-toast-host" :aria-label="t('toast.region')" data-testid="toast-stack">
      <TransitionGroup name="tq-toast" tag="ol" class="tq-toast-host__list">
        <li
          v-for="toast in toasts"
          :key="toast.id"
          class="tq-toast"
          :class="`tq-toast--${toast.tone}`"
          :role="toastRole(toast.tone)"
          :aria-live="toastLiveMode(toast.tone)"
          aria-atomic="true"
          data-testid="toast-item"
        >
          <span class="tq-toast__icon" aria-hidden="true">
            <UIcon :name="toastMeta[toast.tone].icon" />
          </span>
          <span class="tq-toast__copy">
            <span class="tq-toast__eyebrow">{{ t(toastMeta[toast.tone].labelKey) }}</span>
            <strong class="tq-toast__title">{{ toast.title }}</strong>
            <span v-if="toast.message" class="tq-toast__message">{{ toast.message }}</span>
          </span>
          <button class="tq-toast__dismiss" type="button" :aria-label="t('toast.dismiss')" data-testid="toast-dismiss" @click="dismiss(toast.id)">
            <span class="tq-toast__dismiss-label">{{ t('toast.close') }}</span>
            <UIcon name="i-lucide-x" aria-hidden="true" />
          </button>
        </li>
      </TransitionGroup>
    </section>
  </Teleport>
</template>

<style scoped>
.tq-toast-host {
  position: fixed;
  inset-block-start: max(1rem, env(safe-area-inset-top));
  inset-inline-end: max(1rem, env(safe-area-inset-right));
  z-index: 500;
  width: min(27rem, calc(100vw - 2rem));
  pointer-events: none;
}

.tq-toast-host__list {
  display: grid;
  gap: 0.75rem;
  margin: 0;
  padding: 0;
  list-style: none;
}

.tq-toast {
  --toast-tone: var(--tq-focus);
  --toast-bg: color-mix(in oklch, var(--toast-tone) 8%, var(--tq-bg-elevated));
  --toast-border: color-mix(in oklch, var(--toast-tone) 24%, var(--tq-border));
  --toast-icon-bg: color-mix(in oklch, var(--toast-tone) 14%, var(--tq-bg-elevated));
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: start;
  gap: 0.78rem;
  border: 1px solid var(--toast-border);
  border-radius: var(--tq-radius-lg);
  background: var(--toast-bg);
  box-shadow: 0 22px 58px color-mix(in oklch, var(--tq-color-navy) 16%, transparent), var(--tq-shadow-tight);
  color: var(--tq-text);
  padding: 0.85rem;
  pointer-events: auto;
}

.tq-toast--success { --toast-tone: var(--tq-success-readable); --toast-bg: var(--tq-success-bg); }
.tq-toast--error { --toast-tone: var(--tq-danger-readable); --toast-bg: var(--tq-danger-bg); }
.tq-toast--warning { --toast-tone: var(--tq-orange-readable); --toast-bg: var(--tq-warning-bg); }
.tq-toast--info { --toast-tone: var(--tq-info-readable); }

.tq-toast__icon {
  display: grid;
  width: 2.3rem;
  height: 2.3rem;
  place-items: center;
  border: 1px solid color-mix(in oklch, var(--toast-tone) 20%, transparent);
  border-radius: var(--tq-radius-md);
  background: var(--toast-icon-bg);
  color: color-mix(in oklch, var(--toast-tone) 82%, var(--tq-text));
}

.tq-toast__icon :deep(svg) {
  width: 1.15rem;
  height: 1.15rem;
}

.tq-toast__copy {
  display: grid;
  min-width: 0;
  gap: 0.18rem;
  padding-block: 0.08rem;
}

.tq-toast__eyebrow {
  color: color-mix(in oklch, var(--toast-tone) 72%, var(--tq-text));
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  line-height: 1.25;
  text-transform: uppercase;
}

.tq-toast__title {
  font-size: 0.94rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.25;
}

.tq-toast__message {
  color: var(--tq-text-muted);
  font-size: 0.84rem;
  font-weight: 400;
  line-height: 1.45;
  overflow-wrap: anywhere;
}

.tq-toast__dismiss {
  display: inline-flex;
  min-width: 2rem;
  height: 2rem;
  align-items: center;
  justify-content: center;
  gap: 0.32rem;
  border: 1px solid transparent;
  border-radius: var(--tq-radius-pill);
  background: transparent;
  color: var(--tq-text-soft);
  cursor: pointer;
  padding-inline: 0.5rem;
  transition: background 160ms var(--tq-ease-out), color 160ms var(--tq-ease-out), transform 160ms var(--tq-ease-out);
}

.tq-toast__dismiss-label {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.01em;
}

.tq-toast__dismiss:hover {
  background: color-mix(in oklch, var(--toast-tone) 10%, transparent);
  color: var(--tq-text);
  transform: translateY(-1px);
}

.tq-toast__dismiss:active { transform: translateY(0); }

.tq-toast-move,
.tq-toast-enter-active,
.tq-toast-leave-active {
  transition: opacity 190ms var(--tq-ease-out), transform 230ms var(--tq-ease-soft), filter 190ms var(--tq-ease-out);
}

.tq-toast-enter-from,
.tq-toast-leave-to {
  opacity: 0;
  filter: blur(4px);
  transform: translate3d(1rem, -0.35rem, 0) scale(0.985);
}

.tq-toast-leave-active {
  position: absolute;
  width: 100%;
}

@media (max-width: 42rem) {
  .tq-toast-host {
    inset-block-start: max(0.75rem, env(safe-area-inset-top));
    inset-inline: 0.75rem;
    width: auto;
  }

  .tq-toast { padding: 0.78rem; }

  .tq-toast__dismiss-label { display: none; }
}

@media (prefers-reduced-motion: reduce) {
  .tq-toast-move,
  .tq-toast-enter-active,
  .tq-toast-leave-active {
    transition-duration: 0.01ms;
  }

  .tq-toast-enter-from,
  .tq-toast-leave-to {
    filter: none;
    transform: none;
  }
}
</style>
